import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CcEvidenceEvent } from "@/api/code-companion";
import { useCcEvidence, useCcEvidenceEvent } from "@/hooks/useCodeCompanion";
import { tp } from "@/lib/plural";

/**
 * Dossiê de entrega do pacote (CC.34 F8).
 *
 * O ledger é append-only e registra o que de fato aconteceu: promoção, artefato com
 * hash de conteúdo, resultado de deploy por fase, tentativa de correção, evento de
 * transporte, veredito de regressão, checagem de released API.
 *
 * Existia desde o CC.13 e era alcançável **só por tool MCP**, ou seja, só pelo Claude
 * do dev no terminal dele. Era a última das onze tabelas que registram execução do
 * Code Companion sem endpoint HTTP.
 *
 * Duas regras que esta tela não pode quebrar:
 *
 * **O corte aparece.** O backend devolve `total` (tamanho do ledger) e `truncated`.
 * Mostrar 200 eventos como se fossem todos transforma um registro em amostra sem
 * avisar quem lê.
 *
 * **O hash é do conteúdo, não da cadeia.** É sha256 daquele evento, e serve para
 * conferir que ele não mudou. Não prova que a sequência está íntegra, e o rótulo não
 * pode sugerir que prova.
 */

/** Cor por tipo de evento. Desfecho ruim é vermelho; o resto é neutro. */
export function eventTone(event: CcEvidenceEvent): "bad" | "good" | "neutral" {
  const ok = (event.payload as { ok?: unknown } | null)?.ok;
  if (ok === false) return "bad";
  if (event.event_type === "ready_for_deploy") return "good";
  return "neutral";
}

const TONE = {
  bad: "#b02a31",
  good: "#2b8f57",
  neutral: "#6b7280",
};

/**
 * Os campos do payload que valem uma linha, já sem o ruído.
 *
 * `ok` sai porque já virou cor, e repetir "ok: false" ao lado do vermelho é ocupar
 * espaço sem informar.
 */
export function summarize(event: CcEvidenceEvent): string {
  const payload = event.payload ?? {};
  return Object.entries(payload)
    .filter(([k, v]) => k !== "ok" && v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join(" · ");
}

/** Se este evento tem resposta crua do SAP que valha oferecer.
 *
 * ⚠️ **A API mandava e a tela descartava** (#513). `sap_raw` está no tipo desde a CC.34
 * e vinha em toda linha do dossiê; nenhum componente o renderizava. O critério de aceite
 * pede a resposta crua do SAP para quem audita, e ela estava no payload, não na tela.
 *
 * Objeto vazio não conta: oferecer o bloco e abrir para nada é pior que não oferecer.
 */
export function hasSapRaw(event: CcEvidenceEvent): boolean {
  if (event.has_sap_raw !== undefined) return event.has_sap_raw;
  // Fallback para quem já tem o cru em mãos (a leitura de evento único), e para
  // resposta antiga que ainda mandava o cru na lista.
  const raw = event.sap_raw;
  return !!raw && typeof raw === "object" && Object.keys(raw).length > 0;
}

/** A resposta crua formatada para leitura, ou string vazia.
 *
 * Indentada de propósito: uma linha só é ilegível, e quem abre isto está conferindo um
 * veredito contra o que o sistema do cliente respondeu de fato.
 */
export function sapRawText(event: CcEvidenceEvent): string {
  // ⚠️ `JSON.stringify(undefined, null, 2)` devolve o **valor** `undefined`, não a
  // string "undefined", e a função é tipada `: string`. O caminho é real: a linha da
  // lista chega com `has_sap_raw: true` e sem `sap_raw`, então o `hasSapRaw` diz que
  // sim e o cru não está ali. Testar o conteúdo, e não o booleano, fecha.
  const raw = event.sap_raw;
  return raw && typeof raw === "object" ? JSON.stringify(raw, null, 2) : "";
}

/** A resposta crua do SAP, buscada só quando alguém abre.
 *
 * 🔶 **Sob demanda, e não na lista** (#513). O `sap_raw` saiu do dossiê porque o ledger
 * grava uma linha por fase por objeto e a resposta inteira do ADT em toda linha pesa
 * megabytes. Buscar aqui mantém a lista leve e entrega o cru a quem realmente contesta
 * um veredito.
 */
function SapRaw({
  projectId,
  packageId,
  event,
}: {
  projectId: string;
  packageId: number;
  event: CcEvidenceEvent;
}) {
  const { t } = useTranslation();
  const [open, setAberto] = useState(false);
  const detail = useCcEvidenceEvent(projectId, packageId, open ? event.id : undefined);
  return (
    <details
      data-testid="sap-raw"
      className="mt-1"
      onToggle={(ev) => setAberto((ev.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer font-mono text-[10px] text-muted-foreground">
        {t("what the target replied")}
      </summary>
      <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px] leading-snug">
        {detail.isLoading
          ? t("Loading source...")
          : detail.isError
            ? // A mensagem tem de ser sobre o que falhou. Dizer que o objeto não pôde
              // ser lido, quando o objeto está bem e só a busca do cru falhou, manda o
              // auditor investigar a coisa errada.
              t("Could not read the raw target response.")
            : sapRawText(detail.data ?? event)}
      </pre>
    </details>
  );
}

interface Props {
  projectId: string;
  packageId: number;
}

export default function EvidenceDrawer({ projectId, packageId }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useCcEvidence(projectId, packageId);

  if (isError) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("Could not read the delivery dossier.")}
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("Loading dossier...")}
      </div>
    );
  }

  if (!data.events.length) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("Nothing recorded for this package yet.")}
      </div>
    );
  }

  return (
    <div
      data-testid="evidence-drawer"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Delivery dossier")}
        </span>
        <div className="flex-1" />
        {/* O corte é dito, nunca implícito: sem isto a tela mostra uma amostra com
            cara de registro completo. */}
        <span
          data-testid="evidence-count"
          className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
        >
          {data.truncated
            ? t("latest {{shown}} of {{total}} events", {
                shown: data.events.length,
                total: data.total,
              })
            : tp(t, data.total, "1 event", "{{n}} events")}
        </span>
      </div>

      <ul className="max-h-80 divide-y divide-border overflow-y-auto">
        {data.events.map((e) => (
          <li key={e.id} className="px-3 py-2 text-xs">
            <div className="flex flex-wrap items-baseline gap-2">
              <span
                className="font-mono text-[10px] font-semibold uppercase"
                style={{ color: TONE[eventTone(e)] }}
              >
                {e.event_type}
              </span>
              {e.object_name && (
                <span className="font-mono text-[11px]">{e.object_name}</span>
              )}
              <div className="flex-1" />
              {e.created_at && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              )}
            </div>
            {summarize(e) && (
              <div className="mt-0.5 break-words text-[11px] text-muted-foreground">
                {summarize(e)}
              </div>
            )}
            {/* Truncado porque 64 hex numa linha empurra o resto pra fora, e o valor
                aqui é conferir contra o ledger, não ler o número inteiro. */}
            <div
              className="mt-0.5 font-mono text-[10px] text-muted-foreground"
              title={e.hash}
            >
              {t("content hash")} {e.hash.slice(0, 12)}
            </div>
            {/* 🔶 Fechado por padrão: o resumo é o que se lê ao percorrer o dossiê, e o
                cru é o que se abre quando alguém contesta um veredito. Abrir tudo viraria
                parede de JSON e enterraria a linha que interessa. */}
            {hasSapRaw(e) && (
              <SapRaw projectId={projectId} packageId={packageId} event={e} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
